import { useQuery } from '../../../hooks';
import { showToRoles } from '../../../utils';
import {
  yardCompaniesService,
  yardsService,
  clientsService,
  clientYardService,
  belongsToOptions,
} from '../../../services';
import { useStore } from '../../../context';

export const useYardCompaniesQuery = (keys: any = {}) =>
  useQuery(
    ['yard-companies', JSON.stringify(keys)],
    () => {
      return yardCompaniesService.getAll({ params: keys });
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true },
  );

export const useYardCompanyQuery = ({ id }: any) =>
  useQuery(
    [`yard-companies`, id],
    async () => {
      const params: any = { id };

      const res = await yardCompaniesService
        .get({
          params,
        })
        .catch(() => ({}));

      return res;
    },
    { initialData: {} },
  );

export const useYardsQuery = (keys: any = {}) =>
  useQuery(
    ['yards', JSON.stringify(keys)],
    async () => {
      delete keys.companyLegalName;

      return yardsService.getAll({
        params: keys,
      });
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true },
  );

export const useYardQuery = ({ id }: any) =>
  useQuery(
    ['yards', id],
    async () => {
      const params: any = { id };

      const res = await yardsService
        .get({
          params,
        })
        .catch(() => ({}));

      return res;
    },
    { initialData: {} },
  );

const yardOptionsInitialData: { country: any[]; city: any[]; yard: any[] } = {
  country: [],
  city: [],
  yard: [],
};

export const filterYardOptions = (
  data: { country: any[]; city: any[]; yard: any[] },
  country?: string,
  city?: string,
) => {
  let result = data;

  if (country) {
    result = { ...result, city: result.city.filter((obj: any) => country === obj.country) };
  }

  if (country || city) {
    result = {
      ...result,
      yard: result.yard.filter(
        (obj: any) => (!country || country === obj.country) && (!city || city === obj.city),
      ),
    };
  }

  return result;
};

export const useYardOptionsQuery = (
  keys: any = {},
  { user, yardCompanyId, clientId, country, city }: any,
  config?: Parameters<typeof useQuery>[2],
) => {
  const { data } = useQuery<any>(
    ['key', JSON.stringify({ ...keys })],
    () => {
      if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) return yardOptionsInitialData;

      if (
        showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'], user) &&
        !yardCompanyId
      )
        return yardOptionsInitialData;

      if (
        showToRoles(['YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'], user) &&
        yardCompanyId
      )
        return yardsService.options({ yardCompanyId: yardCompanyId });

      if (showToRoles(['SUPER_ADMIN'], user)) return belongsToOptions.Yard('All', true);

      if (clientId) return clientYardService.assignedYards({ id: clientId }, user);

      return yardOptionsInitialData;
    },
    {
      initialData: yardOptionsInitialData,
      ...config,
    },
  );

  return {
    data: filterYardOptions(data, country, city),
  };
};

export const useYardOptionsByClientQuery = (keys: any = {}, props: any) => {
  const result = useYardOptionsQuery(keys, props);

  result.data.country = result.data?.country?.filter((item: any) => !item.isNew);
  result.data.city = result.data?.city?.filter((item: any) => !item.isNew);
  result.data.yard = result.data?.yard?.filter((item: any) => !item.isNew);

  return result;
};

export const useYardBlocksQuery = (keys: any = {}, props: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) {
    props = { yardId: user.yardEmployee.yardId };
  }
  const queryKeys = showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)
    ? props.yardId
    : JSON.stringify({ ...keys });

  Object.keys(props).forEach((key) => {
    if (!props[key]) delete props[key];
  });

  return useQuery(
    ['yard', 'yard-blocks', queryKeys],
    () => {
      if (!props.yardId && !props.containerId && !props.clientYardId) return [];

      return yardsService.getBlocks({ params: { ...props, notLoading: true } });
    },
    { initialData: [] },
  );
};

export const useYardBlocksSceneQuery = (keys: any = {}, props: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) {
    props = { yardId: user.yardEmployee.yardId };
  }
  const queryKeys = showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)
    ? props.yardId
    : JSON.stringify({ ...keys });

  Object.keys(props).forEach((key) => {
    if (!props[key]) delete props[key];
  });

  return useQuery(
    ['yard', 'yard-blocks-scene', queryKeys],
    () => {
      if (!props.yardId && !props.containerId && !props.clientYardId) return '';

      return yardsService.getBlocksScene({ params: { ...props } });
    },
    { initialData: [] },
  );
};

export const useClientsQuery = (keys: any = {}) => {
  if (keys.clientName) delete keys.clientName;

  return useQuery(
    ['clients', JSON.stringify(keys)],
    () => {
      return clientsService.getAll({
        params: keys,
      });
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true },
  );
};

export const useClientQuery = ({ id }: any) =>
  useQuery(
    ['clients', id],
    () => {
      return clientsService
        .get({
          params: { id },
        })
        .then((res) => res.data.data)
        .catch(() => ({}));
    },
    { initialData: {} },
  );
